/** Шаг 4 оформления: сумма и способ оплаты. */
import type {
  CurrentUser,
  PublicSettings,
} from '@arbuz/shared';
import { formatMoney, makeT } from '../../i18n';
import {
  IconCircle,
  type ThemedIconName,
} from '../clientUi';

export function StepPayment({
  user,
  payment,
  total,
  itemCount,
  requisites,
  onPayment,
}: {
  user: CurrentUser;
  payment: 'cash' | 'card_transfer';
  total: number;
  itemCount: number;
  /**
   * Реквизиты для перевода. Это карта АДМИНИСТРАТОРА площадки, а не
   * исполнителя: исполнителю клиент платит наличными, а карта из его кабинета
   * служит для расчётов площадки с ним и наружу не отдаётся вовсе.
   */
  requisites: PublicSettings | null;
  onPayment: (payment: 'cash' | 'card_transfer') => void;
}) {
  const t = makeT(user.locale);

  return (
    <>
      <h1 className="h1">{t('paymentConfirmTitle')}</h1>
      <article className="client-card client-total-card" style={{ marginTop: 'var(--s-4)' }}>
        <p className="muted" style={{ margin: 0 }}>
          {t('payOnDelivery')}
        </p>
        <h2 style={{ margin: 'var(--s-2) 0 0', fontSize: 34, lineHeight: 1.1 }}>
          {formatMoney(String(total), user.locale)} {t('sum')}
        </h2>
        <p className="muted" style={{ margin: 'var(--s-3) 0 0' }}>
          {itemCount} {t('pcs')} · {t('deliveryIncluded')}
        </p>
      </article>

      <h2 style={{ margin: 'var(--s-8) 0 var(--s-3)', fontSize: 'var(--fs-h2)' }}>
        {t('paymentTitle')}
      </h2>
      <div style={{ display: 'grid', gap: 'var(--s-3)' }}>
        <PaymentOption
          icon="cash"
          title={t('paymentCashShort')}
          text={t('paymentCash')}
          selected={payment === 'cash'}
          onClick={() => onPayment('cash')}
        />
        <PaymentOption
          icon="card"
          title={t('paymentCardShort')}
          text={t('paymentCard')}
          selected={payment === 'card_transfer'}
          onClick={() => onPayment('card_transfer')}
        />
      </div>

      {payment === 'card_transfer' && (
        <article className="client-card client-setup-card" style={{ marginTop: 'var(--s-4)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-h3)' }}>{t('cardTransferTitle')}</h3>
          {requisites?.card_requisites ? (
            <>
              <p
                style={{
                  margin: 'var(--s-2) 0 0',
                  font: 'var(--fw-semibold) var(--fs-h3)/var(--lh-h3) var(--font)',
                  // Номер карты переписывают руками — разрывать его переносом
                  // строки посередине нельзя
                  whiteSpace: 'nowrap',
                  overflowX: 'auto',
                }}
              >
                {requisites.card_requisites}
              </p>
              {requisites.card_holder && (
                <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
                  {requisites.card_holder}
                </p>
              )}
              <p className="muted" style={{ margin: 'var(--s-2) 0 0', fontSize: 'var(--fs-sm)' }}>
                {t('cardTransferHint')}
              </p>
            </>
          ) : (
            /* Реквизиты не заполнены администратором — честно об этом говорим,
               иначе клиент подтвердит перевод в никуда */
            <p role="alert" style={{ margin: 'var(--s-2) 0 0', color: 'var(--c-danger)' }}>
              {t('cardTransferMissing')}
            </p>
          )}
        </article>
      )}

      <article
        className="client-card client-setup-card"
        style={{
          marginTop: 'var(--s-8)',
          background: 'var(--c-primary-subtle)',
          borderColor: 'rgb(78 129 53 / 22%)',
        }}
      >
        <div className="client-row" style={{ gridTemplateColumns: '38px 1fr' }}>
          <IconCircle icon="guarantee" />
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--fs-h3)' }}>{t('deliveryContactTitle')}</h2>
            <p className="muted" style={{ margin: 'var(--s-1) 0 0', fontSize: 'var(--fs-sm)' }}>
              {t('deliveryContactText')}
            </p>
          </div>
        </div>
      </article>
    </>
  );
}

function PaymentOption({
  icon,
  title,
  text,
  selected,
  onClick,
}: {
  icon: ThemedIconName;
  title: string;
  text: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className="client-payment-option" onClick={onClick}>
      <IconCircle icon={icon} />
      <span>
        <strong>{title}</strong>
        <span className="muted" style={{ display: 'block', fontSize: 'var(--fs-sm)' }}>
          {text}
        </span>
      </span>
      <span className={`client-radio${selected ? ' active' : ''}`} />
    </button>
  );
}

